import React, { useCallback, useEffect, useState } from 'react';
import config from '../../../../utils/config.js';
import LoadingSpinner from '../../../../components/LoadingSpinner.js';
import Notification from '../../../../components/Notification.js';
import logo from '../../../../assets/images/fullLogo.png';
import logError from '../../../../utils/errorUtil.js';

function formatDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
}

function isCompleted(data) {
    const status = String(data?.End_User_Agreement_Status__c || '').toLowerCase();
    return data?.lastWebhookStatus === 'document.completed' ||
        status === 'document completed' ||
        Boolean(data?.End_User_Submitted_Date__c);
}

export default function EndUserForm() {
    const queryParams = new URLSearchParams(window.location.search);
    const params = {
        id: queryParams.get('id'),
        name: queryParams.get('name'),
        noRecord: queryParams.get('noRecord') === 'true',
    };
    const [salesOrderData, setSalesOrderData] = useState(null);
    const [notification, setNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState({});
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [signingUrl, setSigningUrl] = useState(null);
    const [signingSessionMessage, setSigningSessionMessage] = useState('');
    const [isSigningSessionLoading, setIsSigningSessionLoading] = useState(false);

    const retrieveSalesOrder = useCallback(async () => {
        try {
            const response = await fetch(`${config.origin}/sales/get-sales-order-data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: params.id, salesOrderNumber: params.name, noRecord: params.noRecord }),
            });
            if (!response.ok) throw new Error('Failed to get Sales Order Data');
            const data = await response.json();
            if (!data || data.message === 'Access Denied') throw new Error('Sales Order was not found');
            setSalesOrderData(data);
        } catch (error) {
            logError(error, 'retrieveSalesOrder');
            setNotificationMessage({
                status: 'error',
                title: 'Failed to get Sales Order Data',
                message: 'There was an error retrieving the agreement status. If this issue persists, please contact your sales associate.',
            });
            setNotification(true);
        } finally {
            setIsDataLoaded(true);
        }
    }, [params.id, params.name, params.noRecord]);

    const retrieveSigningSession = useCallback(async () => {
        setIsSigningSessionLoading(true);
        setSigningSessionMessage('');
        try {
            const response = await fetch(
                `${config.origin}/sales/end-user-signing-session?id=${encodeURIComponent(params.id)}&name=${encodeURIComponent(params.name)}`
            );
            if (!response.ok) throw new Error('Failed to get signing session');
            const data = await response.json();
            setSigningUrl(data?.signingUrl || null);
            setSigningSessionMessage(data.message);
        } catch (error) {
            logError(error, 'retrieveSigningSession');
            setSigningUrl(null);
        } finally {
            setIsSigningSessionLoading(false);
        }
    }, [params.id, params.name]);

    useEffect(() => {
        retrieveSalesOrder();
    }, [retrieveSalesOrder]);

    useEffect(() => {
        if (salesOrderData && !isCompleted(salesOrderData)) {
            retrieveSigningSession();
        }
    }, [salesOrderData, retrieveSigningSession]);

    if (!isDataLoaded) return <LoadingSpinner />;
    if (!salesOrderData) {
        return (
            <Notification
                showSavedNotification={notification}
                setShowSavedNotification={setNotification}
                title={notificationMessage.title}
                desc={notificationMessage.message}
                status={notificationMessage.status}
                autoClose={false}
            />
        );
    }

    const completed = isCompleted(salesOrderData);
    const agreementStatus = salesOrderData.End_User_Agreement_Status__c || 'Not yet signed';
    const sentDate = formatDate(salesOrderData.End_User_Form_Sent__c);
    const signedDate = formatDate(salesOrderData.End_User_Submitted_Date__c);
    const pdfEmailDate = formatDate(salesOrderData.completedPdfEmailSentAt);

    return (
        <div className='flex flex-col justify-center items-center p-4 min-h-screen text-center'>
            <Notification
                showSavedNotification={notification}
                setShowSavedNotification={setNotification}
                title={notificationMessage.title}
                desc={notificationMessage.message}
                status={notificationMessage.status}
                autoClose={true}
            />
            <img className='mb-6 w-auto' src={logo} alt='Your Company' />
            <div className='p-6 w-full max-w-2xl bg-white rounded-md border border-gray-200 shadow-sm'>
                <h1 className='mb-2 text-2xl font-medium'>End User Agreement</h1>
                <p className='mb-6 text-gray-600'>Sales Order {salesOrderData.Name || params.name}</p>
                <div className='space-y-3 text-left'>
                    <p><strong>Current status:</strong> {agreementStatus}</p>
                    {sentDate && <p><strong>First sent:</strong> {sentDate}</p>}
                    {signedDate && <p><strong>Signed:</strong> {signedDate}</p>}
                </div>
                {completed ? (
                    <div className='p-4 mt-6 text-green-800 bg-green-50 rounded-md'>
                        <p className='font-medium'>This agreement is complete.</p>
                        {pdfEmailDate && salesOrderData.completedPdfEmailTo ? (
                            <p className='mt-3'>
                                End User Agreement was emailed on {pdfEmailDate} to {salesOrderData.completedPdfEmailTo}. Please refer to that email for a copy of the agreement or contact your sales representative to request a copy.
                            </p>
                        ) : (
                            <p className='mt-3'>
                                A copy of the completed agreement has not yet been emailed. Please contact your sales representative to request a copy.
                            </p>
                        )}
                    </div>
                ) : (
                    <div className='p-4 mt-6 text-blue-800 bg-blue-50 rounded-md'>
                        <p>Your agreement is not complete yet.</p>
                        {isSigningSessionLoading && <p className='mt-2 text-sm'>Preparing your signing link&hellip;</p>}
                        {!isSigningSessionLoading && signingUrl && (
                            <a
                                className='inline-block px-4 py-2 mt-3 font-medium text-white no-underline bg-blue-700 rounded-md'
                                href={signingUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                Sign Agreement
                            </a>
                        )}
                        {signingSessionMessage && (
                            <p className='mt-2 text-sm'>{signingSessionMessage}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
